import map from 'lodash/map';
import uniq from 'lodash/uniq';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import cn from 'classnames';
import values from 'lodash/values';
import forOwn from 'lodash/forOwn';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import TargetGrid from '~/components/TargetGrid/TargetGrid';
import identify from '~/identify';
import css from './css.css';
import {
    getFieldGrid,
    getFieldGridGroupStates,
    getEditFieldValues,
    getItemId,
    getAutocompleteOptions,
    getSubviews,
    getValueAdaptSources,
    getValueAdaptData,
    getItemView,
    getColumns,
    getItemRow,
} from '~/stateGetter';
import TitleRow from './TitleRow';
import {
    presentVisibleEditFields,
    presentElements,
    presentFieldConditionSources,
    reduceFieldSources,
    presentColumnFields,
} from '~/statePresenter';
import { EditField } from '~/ui/Fields/VariadicField/VariadicField';
import { compareKeys, modify } from 'mythil/object';
import filter from 'lodash/filter';
import { connect } from '~/redux/connect';

import Subview from '~/ui/Subview/Subview';
import { iterateGridTargets } from '~/data/targetGrid';
import toOrderedArray from '~/lib/toOrderedArray';
import ElementTooltipContainer from '~/ui/Tooltip/ElementTooltipContainer';
import { TimeCounter } from '~/components/TimeCounter/TimeCounter';
import { Box, ReadonlyField } from '~/native-ui';
import styled from 'styled-components/native';

const ShadowFields = styled(Box)``;

const ToolTippedReadonlyField = ({ ...props }) => {
    return <ReadonlyField {...props} />;
};

const Counter = ({ value, interval, timerDueText, ...props }) => {
    if (!value) {
        return null;
    }

    return (
        <ToolTippedReadonlyField
            {...props}
            renderAsHtml={false}
            value={<TimeCounter interval={interval} dueText={timerDueText} date={value} />}
        ></ToolTippedReadonlyField>
    );
};

class ColumnField extends Component {
    renderCounter() {
        return <Counter {...this.props}></Counter>;
    }

    render() {
        const { interval, timerDueText, ...props } = this.props;

        if (interval && props.value) {
            return this.renderCounter();
        }

        return <ToolTippedReadonlyField {...props} />;
    }
}

const elementMapping = {
    editField: { type: EditField },
    subview: { type: Subview },
    columnField: { type: ColumnField, pass: true },
};

const rowStyleProps = ['level'];
const emptyObject = {};

class ItemGrid extends Component {
    static propTypes = {
        grid: PropTypes.object,
        elements: PropTypes.object,
    };

    static defaultProps = {
        maxLevel: null,
    };

    presentRowTitleRow(row, hasNextGroup) {
        return [{ element: <TitleRow modelId={this.props.modelId} hasNextGroup={hasNextGroup} row={row} /> }];
    }

    renderElements = () => {
        let { elements, modelId } = this.props;
        let renderedElements = {};

        forOwn(elements, (elements, elementType) => {
            return forOwn(elements, (fields, target) => {
                return fields.forEach((field) => {
                    let { name, group } = field;

                    if (!name) {
                        throw new Error('No name provided for element in ' + target);
                    }

                    let elementData = elementMapping[elementType],
                        Element = elementData.type,
                        pass = elementData.pass;

                    let preparedElement = {
                        group,
                        name,
                        element: pass ? (
                            <Element {...field} view={this.props.view} />
                        ) : (
                            <Element modelId={modelId} name={name} view={this.props.view} />
                        ),
                        elementType,
                    };

                    if (!(group in renderedElements)) {
                        renderedElements[group] = [];
                    }

                    renderedElements[group].push(preparedElement);
                });
            });
        });

        return renderedElements;
    };

    computeElementClassNames = (elements) => {
        if (!elements) {
            return null;
        }

        let classNames = {};

        for (let elementData of elements) {
            classNames[css[elementData.elementType + 'Row']] = true;
        }

        return cn(classNames);
    };

    isRowVisible = (row, i, elementsAccumulator) => {
        let name = row.name ? row.name : i;
        let found = false;

        iterateGridTargets([row], (targetName) => {
            if (targetName in elementsAccumulator) {
                found = true;
                return false;
            }
        });

        return found;
    };

    getLevel = (level) => {
        let { maxLevel } = this.props;

        if (this.props.maxLevel !== null) {
            return Math.min(maxLevel, level);
        }

        return level;
    };

    capLevel = (obj) => {
        if (obj.level) {
            obj = modify(obj, 'level', this.getLevel);
        }

        if (obj.style && obj.style.level) {
            obj = modify(obj, 'style.level', this.getLevel);
        }

        return obj;
    };

    prepareRow = (row, elementsAccumulator, hasNextGroup) => {
        let name = row.name;

        row = this.capLevel(row);

        if (name) {
            row.rowClassName = cn(row.rowClassName, this.computeElementClassNames(elementsAccumulator[row.name]));
        } else {
            row.cols = row.cols.map((col) => {
                return {
                    ...this.capLevel(col),
                    rows: this.prepareRows(col.rows, elementsAccumulator, hasNextGroup),
                };
            });
        }

        if (row.title && row.title.length) {
            elementsAccumulator[identify.titleRowIdentifier(name)] = this.presentRowTitleRow(row, hasNextGroup);

            let subRow = omit(row, rowStyleProps),
                styleProps = pick(row, rowStyleProps);

            row = {
                ...styleProps,
                xs: 1,
                cols: [
                    {
                        xs: 12,
                        rows: [
                            {
                                name: identify.titleRowIdentifier(name),
                                xs: 1,
                                elementColClassName: css.titleCol,
                            },
                            subRow,
                        ],
                    },
                ],
            };
        }

        return row;
    };

    prepareRows(rows, elementsAccumulator) {
        let preparedRows = [];
        let rowClassName = this.props.view === identify.VIEW_EDIT ? css.editRow : css.readonlyRow;

        rows = toOrderedArray(rows);

        for (let i = 0; i < rows.length; i++) {
            let row = rows[i],
                hasNextGroup = rows.length >= i + 1;

            if (!this.isRowVisible(row, i, elementsAccumulator)) {
                continue;
            }

            row = { ...row, rowClassName };
            row = this.prepareRow(row, elementsAccumulator, hasNextGroup);

            preparedRows.push(row);
        }

        return preparedRows;
    }

    renderShadowFields() {
        let { modelId, hiddenEditFields } = this.props;

        return (
            <ShadowFields pointerEvents="none" display="none" key="hidden_fields">
                {map(hiddenEditFields, (field, index) => (
                    <EditField
                        index={index}
                        key={field.name}
                        modelId={modelId}
                        name={field.name}
                        isReadonly={true}
                        view={this.props.view}
                    />
                ))}
            </ShadowFields>
        );
    }

    render() {
        let elements = this.renderElements();
        let rows = this.prepareRows(this.props.grid.rows, elements);

        return (
            <React.Fragment key="item_grid">
                {this.renderShadowFields()}
                <TargetGrid
                    key="the_grid_itself"
                    maxLevel={this.props.maxLevel}
                    rows={rows}
                    elements={elements}
                    className={this.props.className}
                />
            </React.Fragment>
        );
    }
}

const areStatePropsEqual = (prev, curr) => {
    // let t1 = performance.now();

    let isEqual = _areStatePropsEqual(prev, curr);

    // let t2 = performance.now();

    // if(!isEqual) {
    // console.log('GRID areStatePropsEqual PERF '+(t2 - t1), diffKeys(prev, curr));
    // }

    return isEqual;
};

const _areStatePropsEqual = (props, prevProps) => {
    return compareKeys(props, prevProps, (prop, prevProp, key) => {
        switch (key) {
            case 'valueAdaptSources':
                return isEqual(prop, prevProp);
            default:
                return prop === prevProp;
        }
    });
};

// deep equal is cheaper than reconcilation
const areMergedPropsEqual = (props, prevProps) => {
    return isEqual(props, prevProps);
};

// !!ATTENTION!! THIS IS PERFORMANCE-CRITICAL, MAKE SURE THAT REDUX-CONNECT CACHING STAYS INTACT
// TODO: OPTIMIZE PERFORMANCE CURRENTLY ON MBP 2012 RECONCILATION OF GRID TAKES ~100-200 ms of
const RXItemGrid = connect(
    (state, { modelId, view, gridName = 'default' }) => {
        const a = 4;

        let props = getValueAdaptData(state, modelId);
        let { valueKeys, optionKeys } = presentFieldConditionSources(props.editFields);

        let valueAdaptFields = values(pick(props.editFields, uniq([...valueKeys, ...optionKeys])));
        let conditionValues = pick(getEditFieldValues(state, modelId), valueKeys);
        props = { ...props, ...getValueAdaptData(state, modelId) };
        props.valueAdaptSources = getValueAdaptSources(state, modelId);
        props.valueAdaptSources = reduceFieldSources(
            valueAdaptFields,
            props.editFields,
            props.filters,
            props.valueAdaptSources
        );
        props.valueAdaptSources.editFieldValues = { ...props.valueAdaptSources.editFieldValues, ...conditionValues };

        let conditionAutocompletes = pick(getAutocompleteOptions(state, modelId, identify.EDITFIELD), optionKeys);
        props.valueAdaptSources.editFieldOptions = {
            ...props.valueAdaptSources.editFieldOptions,
            ...conditionAutocompletes,
        };

        let itemId = getItemId(state, modelId);
        let subviews = itemId && itemId !== identify.NEW_ITEM ? getSubviews(state, modelId) : emptyObject;

        let grid = getFieldGrid(state, modelId, gridName);

        return {
            ...props,
            subviews,
            grid,
            view,
            groupStates: getFieldGridGroupStates(state, modelId),
            itemId: getItemId(state, modelId),
            columns: getColumns(state, modelId),
            itemRow: getItemRow(state, modelId),
        };
    },
    (dispatch) => ({}),
    (props, actionProps, { modelId, ...ownProps }) => {
        const b = 4;

        let { subviews, valueAdaptSources, editFields, columns, itemRow, ...other } = props;

        let visibleEditFields = presentVisibleEditFields({
            ...valueAdaptSources,
            ...props,
        });

        let hiddenEditFields = filter(editFields, (editField, name) => {
            return !(name in visibleEditFields);
        });

        let columnField = {};

        if (itemRow) {
            columnField = presentColumnFields({ columns, itemRow });
        }

        let elements = presentElements({
            editField: visibleEditFields,
            subview: subviews,
            columnField,
        });

        return {
            ...actionProps,
            ...other,
            ...ownProps,
            view: props.view,
            hiddenEditFields,
            elements,
            modelId,
        };
    },
    { areStatePropsEqual, areMergedPropsEqual }
)(ItemGrid);

export default RXItemGrid;
